'use client'

import { useState } from 'react'
import { Button, Image, Spacer, Text, Cell, Validation } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { getFingerprint } from '@marketplace-web/session-management/audit-util'
import { useAsset } from '@marketplace-web/shared/assets'
import {
  transformTwoFactorVerification,
  VerificationType,
  createUser2FA,
  TwoFactorVerificationModel,
} from '@marketplace-web/verification/verification-data'

import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'
import { FaqEntryType } from '@marketplace-web/help-center/help-center-faq-entry-url-data'

import PhoneNumberForm from '../PhoneNumberForm'

type Props = {
  entityId: string
  onConfirm: (params: TwoFactorVerificationModel) => void
}

const AddPhoneNumber = ({ entityId, onConfirm }: Props) => {
  const asset = useAsset('/assets/verifications/add-phone-number')
  const translate = useTranslate('user.two_factor_verification.add_phone_number')
  const userId = useSession().user?.id

  const [isPhoneNumberFormVisible, setIsPhoneNumberFormVisible] = useState(false)
  const [error, setError] = useState<string>()

  const getValidationError = () => error && <Validation text={error} theme="warning" />

  const handleAddPhoneNumberClick = () => {
    setIsPhoneNumberFormVisible(true)
  }

  const handlePhoneConfirm = async (phoneNumber: string) => {
    if (!userId) return

    const response = await createUser2FA({
      userId,
      entityId,
      number: phoneNumber,
      fingerprint: await getFingerprint(),
      entityType: VerificationType.EntityHash,
    })

    if ('errors' in response) {
      const validationError = response.errors[0]

      setError(validationError?.value)

      return
    }

    onConfirm(transformTwoFactorVerification(response))
  }

  const renderFaqLink = () => (
    <HelpCenterFaqEntryUrl
      type={FaqEntryType.AddPhoneNumber}
      render={url => (
        <Button text={translate('actions.faq')} url={url} urlProps={{ target: '_blank' }} />
      )}
    />
  )

  if (isPhoneNumberFormVisible) {
    return (
      <PhoneNumberForm
        onConfirm={handlePhoneConfirm}
        isAccountProtectFlow={false}
        validation={getValidationError()}
      />
    )
  }

  return (
    <Cell>
      <Spacer size={Spacer.Size.X4Large} />
      <div className="u-flexbox u-align-items-center">
        <Image
          src={asset('phone-lock.svg', { theme: { dark: 'dark/phone-lock.svg' } })}
          size={Image.Size.X3Large}
          alt={translate('title')}
          scaling={Image.Scaling.Contain}
        />
      </div>
      <Spacer size={Spacer.Size.X4Large} />
      <Text
        text={translate('title')}
        type={Text.Type.HeadingXL}
        as="h1"
        alignment={Text.Alignment.Center}
        width={Text.Width.Parent}
      />
      <Spacer size={Spacer.Size.Large} />
      <Text text={translate('body')} as="p" />
      <Spacer size={Spacer.Size.X2Large} />
      <Button
        text={translate('actions.add_phone')}
        onClick={handleAddPhoneNumberClick}
        styling={Button.Styling.Filled}
      />
      <Spacer size={Spacer.Size.Large} />
      {renderFaqLink()}
    </Cell>
  )
}

export default AddPhoneNumber
